import ImageDialog from "@/components/Dialog/ImageDialog/ImageDialog";
import Icon from "@/components/Icon/Icon";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { defaultNS, useTranslation } from "@/lib/i18n";
import {
  CONFIDENTIAL_FILE_ICON,
  isHighlyConfidentialFile,
} from "@/lib/security/confidential-utils";
import type { ReactNode } from "react";
import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type FileValue =
  | {
      s3Url: string;
      originalName: string;
    }
  | null
  | undefined;

type Props = CFormItemProps & {
  value?: FileValue;
  readComponent?: ReactNode;
  onChange?: (value?: FileValue) => void;
  title?: string;
  showFileName?: boolean;
};

/**
 * Card-based Form / FileItem
 * ファイル（主に画像）を表示し、クリックでImageDialogを開く
 */
export const CFormFileItem = buildCFormItem((props: Props) => {
  const { value, title, showFileName = true } = props;
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "common.fileItem",
  });

  const readComponent = props.readComponent ?? (
    <HStack className="gap-2 items-center">
      {value?.s3Url ? (
        <>
          {isHighlyConfidentialFile(value.s3Url) ? (
            <HStack className="gap-2 items-center text-neutral-muted-on-fill">
              <Icon
                iconName={CONFIDENTIAL_FILE_ICON}
                className="w-4 h-4 text-neutral-muted-on-fill"
              />
              {showFileName && (
                <Text
                  variant="labelSemiLarge"
                  className="text-neutral-muted-on-fill"
                >
                  {value.originalName || t("unknownFileName")}
                </Text>
              )}
            </HStack>
          ) : (
            <>
              <ImageDialog
                title={title || value.originalName || t("defaultImageTitle")}
                image={{
                  url: value.s3Url,
                  alt: value.originalName || t("defaultImageTitle"),
                }}
              />
              {showFileName && (
                <Text variant="labelSemiLarge" className="text-neutral-on-fill">
                  {value.originalName || t("unknownFileName")}
                </Text>
              )}
            </>
          )}
        </>
      ) : (
        <HStack className="gap-2 items-center text-neutral-muted-on-fill">
          <Icon
            iconName="ImageIcon"
            className="w-5 h-5 text-neutral-muted-on-fill"
          />
          <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
            {t("noFile")}
          </Text>
        </HStack>
      )}
    </HStack>
  );

  // TODO: 編集用のコンポーネントを作成しないとね〜

  return <CFormItemBase {...props} readComponent={readComponent} />;
});
